import axios from "axios";
import store from "@/store/index";
import router from "../router";
import Vue from "vue";
import { v4 as uuidv4 } from "uuid"; // Make sure to import uuid
const { buildApiUrl } = require("./urlBuilder.cjs");
const { isPublicRouteName } = require("@/router/authNavigation.cjs");

const toastDuration = 8000;
const baseURL = process.env.VUE_APP_GOAL_BANK_PATH;

const HttpMethods = Object.freeze({
  GET: "get",
  POST: "post",
  DELETE: "delete",
  PUT: "put",
	PATCH: 'patch',

});

const visibleMessages = new Set();
function showToast(message, type, duration) {
  if (!visibleMessages.has(message)) {
    visibleMessages.add(message);

    Vue.$toast.open({
      message: message,
      type: type,
      duration: duration,
      position: "top",
    });

    setTimeout(() => {
      visibleMessages.delete(message);
    }, toastDuration);
  }
}

function clearLocalData() {
  localStorage.removeItem("Gback");
  localStorage.removeItem("cp_pwa");
  store.commit("userConfig/clearAllConfigs");
}

function getThrottleMessage(detail) {
  const secondsMatch = String(detail || "").match(/(\d+)\s*seconds?/i);
  if (!secondsMatch) {
    return "تعداد درخواست‌ها بیش از حد مجاز است. لطفاً کمی بعد دوباره تلاش کنید.";
  }

  const totalMinutes = Math.max(1, Math.ceil(Number(secondsMatch[1]) / 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const number = new Intl.NumberFormat("fa-IR");
  const waitParts = [];

  if (hours) waitParts.push(`${number.format(hours)} ساعت`);
  if (minutes) waitParts.push(`${number.format(minutes)} دقیقه`);

  return `تعداد درخواست‌ها بیش از حد مجاز است. لطفاً ${waitParts.join(" و ")} دیگر تلاش کنید.`;
}

function handleError({ status = 0, data = {} } = {}, isAuthorized = true) {
  switch (status) {
    case 401:
      if (!isAuthorized) break;
      clearLocalData();
      if (isPublicRouteName(router.currentRoute.name)) break;
      if (router.currentRoute.name !== "login") {
        router.replace({
          name: "login",
          query: { redirect: router.currentRoute.fullPath },
        }).catch(() => {});
      }
      break;
    // A 403 means the authenticated user cannot perform this operation; it
    // must not destroy an otherwise valid session (notably on handoff links).
    case 403:
      break;
  }

  const message = status === 429
    ? getThrottleMessage(data.detail)
    : data.detail ||
      "امکان ارتباط با سرور وجود ندارد. لطفا اتصال سرویس را بررسی کنید.";
  showToast(message, "default", toastDuration);
}

let refreshPromise = null;
async function refreshAccessToken() {
  const refresh = store.state.userConfig.refreshToken;
  if (!refresh) return false;
  if (!refreshPromise) {
    refreshPromise = axios.post(
      buildApiUrl(baseURL, "auth", "/api/token/refresh/"),
      { refresh }
    ).then((response) => {
      store.commit("userConfig/setAccessToken", `Bearer ${response.data.access}`);
      if (response.data.refresh) store.commit("userConfig/setRefreshToken", response.data.refresh);
      return true;
    }).catch(() => false).finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

async function apiRequest({
  method,
  url,
  service,
  headers,
  data,
  queryString,
  isAuthorized,
  deviceId, // Add deviceId parameter
  retried = false,
}) {
  const apiUrl = buildApiUrl(baseURL, service, url);

  const token = store.state.userConfig.accessToken;
  // Add custom headers
  headers = {
    ...headers,
    "DEVICE-CLIENT-USER": deviceId,
    "CLIENT-USER-REFERER": store.state.userConfig.refValue,
    ...(token && isAuthorized && { Authorization: token }),
  };

  try {
    switch (method) {
      case HttpMethods.GET:
        return await axios.get(apiUrl + queryString, {
          headers: headers,
        });
      case HttpMethods.POST:
        return await axios.post(apiUrl, data, {
          headers: headers,
        });
      case HttpMethods.DELETE:
        return await axios.delete(apiUrl, {
          headers: headers,
        });
      case HttpMethods.PUT:
        return await axios.put(apiUrl, data, {
          headers: headers,
        });
        case HttpMethods.PATCH:
				return await axios.patch(apiUrl, data, {
					headers: headers,
				});
      default:
        throw new Error("Unsupported HTTP method");
    }
  } catch (error) {
    const response = error.response || {
      status: 0,
      data: {
        detail:
          error.message ||
          "امکان ارتباط با سرور وجود ندارد. لطفا اتصال سرویس را بررسی کنید.",
      },
    };
    if (response.status === 401 && isAuthorized && !retried && await refreshAccessToken()) {
      return apiRequest({ method, url, service, headers, data, queryString, isAuthorized, deviceId, retried: true });
    }
    handleError(response, isAuthorized);
    return response;
  }
}

// Add deviceId handling here
const apiService = {
  deviceId: null, // Initialize deviceId

  constructor() {
    this.deviceId = this.getDeviceId();
  },

  getDeviceId() {
    let deviceId = store.state.userConfig.deviceIds;
    if (!deviceId) {
      deviceId = uuidv4();
      store.commit("userConfig/setdeviceIds", deviceId);
    }
    return deviceId;
  },

  error_code: null,
  get: async function (url, service = "", headers = {}, queryString = "", isAuthorized = true) {
    return await apiRequest({
      method: HttpMethods.GET,
      url: url,
      service: service,
      headers: headers,
      queryString: queryString,
      isAuthorized: isAuthorized,
      deviceId: this.deviceId, // Use this.deviceId
    });
  },
  post: async function (url, service = "", data, headers = {}, isAuthorized = true) {
    return await apiRequest({
      method: HttpMethods.POST,
      url: url,
      service: service,
      headers: headers,
      data: data,
      isAuthorized: isAuthorized,
      deviceId: this.deviceId, // Use this.deviceId
    });
  },
  delete: async function (url, service = "", headers = {}, isAuthorized = true) {
    return await apiRequest({
      method: HttpMethods.DELETE,
      url: url,
      service: service,
      headers: headers,
      isAuthorized: isAuthorized,
      deviceId: this.deviceId, // Use this.deviceId
    });
  },
  put: async function (url, service = "", data, headers = {}, isAuthorized = true) {
    return await apiRequest({
      method: HttpMethods.PUT,
      url: url,
      service: service,
      headers: headers,
      data: data,
      isAuthorized: isAuthorized,
      deviceId: this.deviceId, // Use this.deviceId
    });
  },
  patch: async (url, service = '', data, headers = {}, isAuthorized = true) => {
		return await apiRequest({
			method: HttpMethods.PATCH,
			url: url,
			service: service,
			headers: headers,
			data: data,
			isAuthorized: isAuthorized,
		});
	},
};

// Call the constructor to initialize deviceId
apiService.constructor();

export default apiService;
